import {
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import { PaymentIntent, StripePaymentElementOptions } from "@stripe/stripe-js";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";

import { handleSetPaymentMethod } from "../userDashboard/userControllers/cardController";

import load from "./assets/load.gif";
import { buildStripeReturnUrl } from "./stripeReturnUrl";

interface PayformProps {
    clientSecret: string;
    transactionId: string;
    payment_method?: string;
    onSuccess?: (paymentIntent: PaymentIntent | { payment_intent: PaymentIntent; message: string }) => void;
    onError?: (error: unknown) => void;
}

const Payform: React.FC<PayformProps> = ({
    clientSecret,
    transactionId,
    payment_method = "card",
    onSuccess,
    onError
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [paymentAttempted, setPaymentAttempted] = useState<boolean>(false);
    const [saveCard, setSaveCard] = useState<boolean>(false);
    const reportedIntentRef = useRef<string>("");

    const reportIntent = useCallback((paymentIntent: PaymentIntent) => {
        if (reportedIntentRef.current === paymentIntent.id) {
            return;
        }

        reportedIntentRef.current = paymentIntent.id;
        onSuccess?.({
            payment_intent: paymentIntent,
            message: paymentIntent.status === "succeeded"
                ? "Payment completed successfully"
                : "Payment is processing"
        });
    }, [onSuccess]);

    useEffect(() => {
        if (!stripe || !clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent, error }) => {
            if (error) {
                setMessage(error.message || "Unable to load the secure payment.");
                return;
            }

            if (!paymentIntent) return;

            if (paymentIntent.status === "succeeded") {
                setMessage("Payment succeeded!");
                reportIntent(paymentIntent);
            } else if (paymentIntent.status === "processing") {
                setMessage("Your bank is processing the payment. This page will update automatically.");
                reportIntent(paymentIntent);
            } else if (paymentIntent.status === "requires_action") {
                setMessage("Complete your bank verification to finish this payment.");
            } else if (paymentIntent.status === "requires_payment_method") {
                setMessage(
                    paymentAttempted
                        ? "This card payment was not completed. Try again or use another card."
                        : null
                );
            } else if (paymentIntent.status === "canceled") {
                setMessage("This payment attempt was cancelled.");
            }
        });
    }, [stripe, clientSecret, paymentAttempted, reportIntent]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsLoading(true);
        setMessage(null);
        setPaymentAttempted(true);

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: buildStripeReturnUrl(transactionId, payment_method),
                },
                redirect: "if_required",
            });

            if (error) {
                if (error.type === "card_error" || error.type === "validation_error") {
                    setMessage(error.message || "The card payment could not be completed.");
                } else {
                    setMessage("Stripe could not complete the payment. Please try again.");
                }
                onError?.(error);
                return;
            }

            if (!paymentIntent) {
                setMessage("Stripe did not return a payment status. Please retry.");
                return;
            }

            if (paymentIntent.status === "succeeded") {
                setMessage("Payment succeeded!");

                if (saveCard && paymentIntent.payment_method) {
                    await new Promise((resolve) => {
                        handleSetPaymentMethod(
                            { payment_method_id: paymentIntent.payment_method },
                            resolve,
                            resolve
                        );
                    });
                }

                reportIntent(paymentIntent);
            } else if (paymentIntent.status === "processing") {
                setMessage("Your bank is processing the payment. We will confirm it automatically.");
                reportIntent(paymentIntent);
            } else if (paymentIntent.status === "requires_action") {
                setMessage("Bank verification was not completed. Select Pay now to try again.");
            } else if (paymentIntent.status === "requires_payment_method") {
                setMessage(
                    paymentIntent.last_payment_error?.message
                    || "This card payment was not completed. Try again or use another card."
                );
            } else {
                setMessage("This payment is not complete yet.");
            }
        } catch (error: unknown) {
            setMessage(error instanceof Error
                ? error.message
                : "An unexpected payment error occurred.");
            onError?.(error);
        } finally {
            setIsLoading(false);
        }
    };

    const paymentElementOptions: StripePaymentElementOptions = {
        layout: "tabs",
    };

    return (
        <form id="payment-form" className=" mt-6 w-full" onSubmit={handleSubmit}>
            <PaymentElement
                className=" font-Manrope w-full"
                id="payment-element"
                options={paymentElementOptions}
            />

            <div className="flex items-center mt-4">
                <input
                    id="save-card-checkbox"
                    type="checkbox"
                    checked={saveCard}
                    onChange={(e) => setSaveCard(e.target.checked)}
                    className="w-4 h-4 text-[#317333] border-gray-300 rounded focus:ring-[#317333]"
                />
                <label htmlFor="save-card-checkbox" className="ml-2 block text-sm text-gray-900 font-Manrope">
                    Save for future use
                </label>
            </div>

            <button
                className=" bg-[#317333] rounded-md py-3 font-Manrope text-[#fff] font-semibold  text-sm  md:text-base flex w-full mt-4 items-center justify-center"
                disabled={isLoading || !stripe || !elements}
                id="submit"
            >
                <span id="button-text">
                    {isLoading ? <img src={load} className=" w-5" alt="" /> : "Pay now"}
                </span>
            </button>
            {message && (
                <div
                    className=" font-Manrope text-sm font-medium w-full text-left mt-3"
                    id="payment-message"
                >
                    {message}
                </div>
            )}
        </form>
    );
};

export default Payform;
